import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { TruckDriverItemDelete, TruckDriversInitialState, TruckDriversLoad } from './truck-drivers.actions';
import { LIST_SERVER_PAGINATION_INITIAL_STATE, ListServerPaginationStateModel } from '../../../../../common/models';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { TruckDriversService } from './truck-drivers.service';
import { TruckDriverBrief } from '../models';
import { Error, CustomHttpErrorResponse } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export type TruckDriversStateModel = ListServerPaginationStateModel<TruckDriverBrief>;

const initialState: TruckDriversStateModel = LIST_SERVER_PAGINATION_INITIAL_STATE;

@State<TruckDriversStateModel>({
  name: 'TruckDrivers',
  defaults: initialState,
})
@Injectable()
export class TruckDriversState {
  constructor(private truckDriversService: TruckDriversService, private notificationService: NotificationService) {}

  @Selector()
  static getState(state: TruckDriversStateModel): TruckDriversStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: TruckDriversStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getItems(state: TruckDriversStateModel): TruckDriverBrief[] {
    return state.items;
  }

  @Action(TruckDriversLoad)
  getTruckDrivers(
    ctx: StateContext<TruckDriversStateModel>,
    { payload }: TruckDriversLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.truckDriversService.getTruckDrivers(payload).pipe(
      take(1),
      map((list: TruckDriverBrief[]) => {
        ctx.patchState({
          items: [...list],
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TruckDriverItemDelete)
  deleteTruckDriver(
    ctx: StateContext<TruckDriversStateModel>,
    { payload }: TruckDriverItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.truckDriversService
      .deleteTruckDriver(payload.hubId, payload.frequentDriverIds, payload.truckIds, payload.tourExecutionIds)
      .pipe(
        take(1),
        map(() => {
          payload.dialog.close();
          this.notificationService.success('api.driverList.success.delete');
          return ctx.dispatch(new TruckDriversInitialState());
        }),
        catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
      );
  }

  @Action(TruckDriversInitialState)
  setInitialState(ctx: StateContext<TruckDriversStateModel>): void {
    ctx.setState(initialState);
  }
}
